import {
    cloneDeep,
    get,
    set,
    forOwn,
    join,
} from 'lodash';
import { generateString } from './schemaGenerators';
import Dot from 'dot-object';

const SEPARATOR = '__';

/**
 * This conversion will work on any property with `title` and `description`
 * @param {any} property 
 * @param {string} id 
 */
export function convertGenericToEditMode(property, id) {
    const title = id + ' Title';
    const description = id + ' Description';
    const base_id = 'properties' + SEPARATOR + id + SEPARATOR;
    const title_property = generateString({
        title: title,
        id: base_id + 'title',
    });

    const description_property = generateString({
        title: description,
        'X-Format': 'textarea',
        'X-Encode': false,
        id: base_id + 'description',
    });
    // boolean properties
    let p = {
        [title_property.id]: title_property,
        [description_property.id]: description_property,
    };
    // boolean order
    let order = [
        title_property.id,
        description_property.id,
    ];

    let form_data = {
        [title_property.id]: property.title,
        [description_property.id]: property.description || '',
    };

    return [order, p, form_data];
}

export function convertRadioToEditMode(property, id) {
    const base_id = join(['properties', id], SEPARATOR);
    let [
        order,
        p,
        form_data,
    ] = convertGenericToEditMode(property, id);

    if (property.hasOwnProperty('X-Options')) {
        get(property, ['X-Options', 'enumOptions'], [])
            .forEach((option, index) => {
                const base_option_id = join([
                    base_id,
                    'X-Options',
                    'enumOptions',
                    index,
                ], SEPARATOR)

                const title_property = generateString({
                    title: 'Label ' + index,
                    id: base_option_id + SEPARATOR + 'label',
                });
                
                p[title_property.id] = title_property;
                order.push(title_property.id);
                form_data[title_property.id] = option.label;

                switch (property['X-Format']) {
                    case 'side-by-side': {
                        const description_property = generateString({
                            title: 'Description ' + index,
                            'X-Format': 'textarea',
                            'X-Encode': false,
                            id: base_option_id + SEPARATOR + 'description',
                        });
                        p[description_property.id] = description_property;
                        order.push(description_property.id);
                        form_data[description_property.id] = option.description || '';
                        break;
                    }
                    default:
                        break;
                }
            });
    }

    return [order, p, form_data];
}

export function convertCourseInfoToEditMode(property, id) {
    const base_id = join(['properties', id], SEPARATOR);
    let [
        order,
        p,
        form_data,
    ] = convertGenericToEditMode(property, id);

    if (property.hasOwnProperty('X-Links')) {
        get(property, ['X-Links'], [])
            .forEach((link, index) => {
                const base_link_id = join([
                    base_id,
                    'X-Links',
                    index,
                ], SEPARATOR)

                const title_property = generateString({
                    title: 'Link Title ' + index,
                    id: base_link_id + SEPARATOR + 'title',
                });
                
                p[title_property.id] = title_property;
                order.push(title_property.id);
                form_data[title_property.id] = link.title;

                const content_property = generateString({
                    title: 'Description ' + index,
                    'X-Format': 'textarea',
                    'X-Encode': false,
                    id: base_link_id + SEPARATOR + 'content',
                });
                p[content_property.id] = content_property;
                order.push(content_property.id);
                form_data[content_property.id] = link.content || '';
            });
    }

    return [order, p, form_data];
}

export function convertSurveyTableToEditMode(property, id) {
    const base_id = join(['properties', id], SEPARATOR);
    let [
        order,
        p,
        form_data,
    ] = convertGenericToEditMode(property, id);

    if (property.hasOwnProperty('X-Options')) {
        get(property, ['X-Options', 'rows'], [])
            .forEach((row, row_index) => {
                row.cols.forEach((col, col_index) => {
                    const label_id = join([
                        base_id,
                        'X-Options',
                        'rows',
                        row_index,
                        'cols',
                        col_index,
                        'label',
                    ], SEPARATOR)

                    const label_property = generateString({
                        title: `Row: ${row_index} Col: ${col_index}`,
                        id: label_id,
                        'X-Format': 'textarea',
                        'X-Encode': false,
                    });


                    p[label_property.id] = label_property;

                    order.push(label_property.id);

                    form_data[label_property.id] = col.label;
                })
            });
    }

    return [order, p, form_data];
}

export function convertPropertiesToEditMode(schema) {
    // new properties
    let p = {};
    // new order
    let order = [];
    let form_data = {};

    for (let index = 0; index < schema['X-Order'].length; index++) {
        const id = schema['X-Order'][index];
        if (!schema.properties.hasOwnProperty(id)) {
            continue;
        }
        const property = schema.properties[id];
        switch (property.type) {
            case 'radio': {
                const [
                    property_order,
                    property_properties,
                    property_form_data,
                ] = convertRadioToEditMode(property, id);
                p = {
                    ...p,
                    ...property_properties,
                };
                form_data = {
                    ...form_data,
                    ...property_form_data,
                };
                order = order.concat(property_order);
                break;
            }
            case 'survey_table': {
                const [
                    property_order,
                    property_properties,
                    property_form_data,
                ] = convertSurveyTableToEditMode(property, id);
                p = {
                    ...p,
                    ...property_properties,
                };
                form_data = {
                    ...form_data,
                    ...property_form_data,
                };
                order = order.concat(property_order);
                break;
            }
            case 'course_info': {
                const [
                    property_order,
                    property_properties,
                    property_form_data,
                ] = convertCourseInfoToEditMode(property, id);
                p = {
                    ...p,
                    ...property_properties,
                };
                form_data = {
                    ...form_data,
                    ...property_form_data,
                };
                order = order.concat(property_order);
                break;
            }
            case 'text':
            case 'file':
            case 'string':
            case 'boolean': {
                const [
                    property_order,
                    property_properties,
                    property_form_data,
                ] = convertGenericToEditMode(property, id);
                p = {
                    ...p,
                    ...property_properties,
                };
                form_data = {
                    ...form_data,
                    ...property_form_data,
                };
                order = order.concat(property_order);
                break;
            }
            default:
                p[id] = property;
                order.push(id);
                break;
        }
    }


    return [order, p, form_data];
}

/**
 * 
 * @param {any} schema Assessment schema to convert
 * @returns [Schema, FormData]
 */
export function convertSchemaToEditMode(schema) {
    const [order, p, f] = convertPropertiesToEditMode(schema);
    const form_data = {
        ...f,
        ...getEditModeFormData(schema, p),
    };

    let s = {
        'X-Order': [
            'title',
            'description',
        ].concat(order),
        description: '',
        id: schema.id,
        type: 'page',
        properties: {
            'title': generateString({
                title: 'Page Title',
            }),
            'description': generateString({
                title: 'Page Description',
                'X-Format': 'textarea',
                'X-Encode': false,
            }),
            ...p,
        }
    };

    return [s, form_data];
}

export function getEditModeFormData(schema) {

    const description = schema.description || '';

    return {
        'title': schema.title,
        'description': description,
    };
}

/**
 * When saving an edited schema, merge the edit mode form data 
 * with the schema to finish the editing process
 * @param { {schema: any, form_data: any, page_schema: any} } options 
 */
export function mergeEditModeFormData(options) {
    const {
        schema,
        form_data,
        page_schema,
        path_types
    } = options;

    if (!page_schema.hasOwnProperty('id') || get(schema, ['properties', page_schema.id]) === undefined) {
        console.warn(`Could not find id: ${page_schema.id} in schema`);
        return schema;
    }

    const id = page_schema.id;

    let s = cloneDeep(schema);

    let dotted_obj = cloneDeep(form_data);
    let dot = new Dot(SEPARATOR);
    dot.object(dotted_obj);

    // set each path type and remove it from `dotted_obj`
    forOwn(path_types, (value, key) => {
        const page_data = get(dotted_obj, ['properties', key]);
        if (page_data === undefined) {
            return;
        }

        const path = value['X-Source'].split('.');
        forOwn(page_data, (page_v, page_k) => {
            let v = page_v;

            const sliced_path = path.slice(1);
            set(s, [...sliced_path, page_k], v);
        });

        delete dotted_obj.properties[key];
    });

    let form_data_with_paths_removed = {};
    dot.dot(dotted_obj, form_data_with_paths_removed)

    forOwn(form_data_with_paths_removed, (value, key) => {

        let v = cloneDeep(value);
        const path = key.split(SEPARATOR);

        if (path.length < 1) {
            return;
        }

        set(s, ['properties', id, ...path], v);
    });

    return s;
}



// WEBPACK FOOTER //
// ./src/utils/editSchemaGenerators.js